/**
 * A handful of screens and a query string do not need a routing library. This is the
 * History API behind a `useSyncExternalStore`, so every component that reads the
 * location re-renders on a push, a replace and the back button alike.
 */
import {
  type AnchorHTMLAttributes,
  type MouseEvent,
  useCallback,
  useSyncExternalStore,
} from 'react';

const NAVIGATIE_EVENT = 'mamimap:navigatie';

export const ROUTES = {
  kaart: '/kaart',
  kansjes: '/kansjes',
  /** De kansjes per fase, van gescout tot bod geaccepteerd (MA-51). */
  kanban: '/kanban',
  /** Een woning taxeren met referenties (MA-56); altijd met `?woning=<id>`. */
  taxatie: '/taxatie',
  /** Nieuwe energielabels in het zoekgebied, per buurt en per dag (MA-61). */
  labels: '/labels',
  datasets: '/datasets',
  instellingen: '/instellingen',
  /** De Funda-ronde op de server: starten, voortgang, en een captcha oplossen. */
  ronde: '/ronde',
  /** De werkruimte SportCity (MA-72): de kaart met de clubs. */
  sportcity: '/sportcity',
  /**
   * Inloggen met `?terug=`: daarna door naar die URL, als hij op mamimap.nl staat (MA-44,
   * het venster van de vanger). Wie niet ingelogd is ziet op elk pad het inlogformulier.
   */
  inloggen: '/inloggen',
} as const;

/** De schermen die het filter in de URL delen; Instellingen doet daar niet aan mee. */
export const SCHERMEN: readonly Route[] = [
  ROUTES.kaart,
  ROUTES.kansjes,
  ROUTES.kanban,
  ROUTES.datasets,
];

export type Route = (typeof ROUTES)[keyof typeof ROUTES];

/** Waar de taxatie van een woning staat (MA-56). */
export function taxatieUrl(woningId: string): string {
  return `${ROUTES.taxatie}?woning=${woningId}`;
}

const abonnees = new Set<() => void>();

function abonneer(melden: () => void): () => void {
  abonnees.add(melden);
  window.addEventListener('popstate', melden);
  window.addEventListener(NAVIGATIE_EVENT, melden);
  return () => {
    abonnees.delete(melden);
    window.removeEventListener('popstate', melden);
    window.removeEventListener(NAVIGATIE_EVENT, melden);
  };
}

function huidigeUrl(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/** The current path plus query string; changes on every navigation. */
export function useUrl(): string {
  return useSyncExternalStore(abonneer, huidigeUrl, () => ROUTES.kaart);
}

export function usePad(): string {
  const url = useUrl();
  return url.split('?')[0] ?? '/';
}

export function useZoekParams(): URLSearchParams {
  const url = useUrl();
  return new URLSearchParams(url.split('?')[1] ?? '');
}

/** Pushes (or replaces) a url and tells every subscriber. */
export function navigeer(url: string, opties: { vervang?: boolean } = {}): void {
  if (url === huidigeUrl()) return;
  if (opties.vervang) {
    window.history.replaceState(null, '', url);
  } else {
    window.history.pushState(null, '', url);
  }
  window.dispatchEvent(new Event(NAVIGATIE_EVENT));
}

/** Keeps the query string, swaps the screen: filters survive a screen change. */
export function useNaarScherm(): (route: Route) => string {
  const zoek = useZoekParams().toString();
  return useCallback((route: Route) => (zoek ? `${route}?${zoek}` : route), [zoek]);
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { naar: string; vervang?: boolean };

export function Link({ naar, vervang, onClick, ...rest }: LinkProps) {
  function klik(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigeer(naar, { vervang });
  }
  return <a href={naar} onClick={klik} {...rest} />;
}
